import { nextTick } from "vue";

/**
 * Commits a pick from the picker's `update-selected` event, whose first argument is the selection
 * array: writes the model, reports `input` then `change`, and closes the menu unless
 * `keepOpenOnSelect`.
 *
 * @param {{ model: import("vue").Ref<any[]>, selection: import("vue").ComputedRef<any>,
 *   keepOpenOnSelect: import("vue").Ref<boolean>, promptNormalized: (entries: any[]) => any[],
 *   commitCustomItem: (values: any[], previousValues: any[]) => any[], closeMenu: () => void,
 *   emitBubbling: (name: string, detail: any) => void,
 *   form: ReturnType<typeof import("#ce/composables/useForm").useForm> }} options
 *   `form` is the host's `useForm()`.
 * @returns {{ handleUpdateSelected: (hostEvent: CustomEvent) => void }}
 */
export function useSelectCommit({
  model,
  selection,
  keepOpenOnSelect,
  promptNormalized,
  commitCustomItem,
  closeMenu,
  emitBubbling,
  form,
}) {
  function handleUpdateSelected(hostEvent) {
    const { detail } = hostEvent;

    const [values] = detail;
    model.value = promptNormalized(commitCustomItem(values, model.value));
    // Emitted here, where the user's pick enters, rather than from a watcher on the model: a watcher
    // also fires for attribute syncs, property writes and form resets — which is both wrong (`input`
    // reports user input, as a native control does) and a flush late, long enough for a host's value
    // binding to be re-applied with the value the pick has not reached yet.
    emitBubbling("input", selection.value);
    // A native select reports a pick with `input` then `change`, both at the pick rather than on
    // blur, and `multiple` dispatches on every pick that lands.
    form.emitChange();

    if (keepOpenOnSelect.value) {
      return;
    }

    // This is required to close the menu after a selection is made and all events have been handled.
    nextTick(closeMenu);
  }

  return {
    handleUpdateSelected,
  };
}
